import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, Button, Alert, TextField,
  InputAdornment, CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import Sidebar, { SIDEBAR_WIDTH } from '../components/Sidebar';
import { useIsMobile } from '../hooks/useIsMobile';
import api from '../api/axios';

export default function DeviceRegistrationsPage() {
  const isMobile = useIsMobile();
  const [alerts, setAlerts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  useEffect(() => { fetchAlerts(); }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(alerts);
      return;
    }
    const q = search.toLowerCase();
    setFiltered(alerts.filter(a =>
      a.full_name?.toLowerCase().includes(q) ||
      a.username?.toLowerCase().includes(q) ||
      a.device_name?.toLowerCase().includes(q)
    ));
  }, [search, alerts]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/alerts/devices/');
      setAlerts(Array.isArray(data) ? data : []);
    } catch {
      setMsg({ type: 'error', text: 'Failed to load device registrations.' });
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/alerts/devices/read-all/');
      setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
      setMsg({ type: 'success', text: 'All alerts marked as read.' });
    } catch {
      setMsg({ type: 'error', text: 'Failed to mark alerts as read.' });
    }
  };

  const unreadCount = alerts.filter(a => !a.is_read).length;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f4f6f4' }}>
      <Sidebar />
      <Box sx={{
        ml: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
        p: isMobile ? 2 : 3,
        pt: isMobile ? 7 : 3,
        minHeight: '100vh',
        flex: 1,
        minWidth: 0,
        overflowX: 'hidden',
      }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>Device Registrations</Typography>
            <Typography sx={{ color: '#888', fontSize: 13, mt: 0.5 }}>{today}</Typography>
          </Box>
          {unreadCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={markAllRead}
              sx={{ borderColor: '#1a4d2e', color: '#1a4d2e', borderRadius: 2 }}
            >
              Mark all read ({unreadCount})
            </Button>
          )}
        </Box>

        {msg.text && (
          <Alert severity={msg.type} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMsg({ type: '', text: '' })}>
            {msg.text}
          </Alert>
        )}

        <Card>
          {/* Search bar */}
          <Box sx={{ p: 2, background: '#fafcfa', borderBottom: '0.5px solid #e8ede8' }}>
            <TextField
              placeholder="Search by name, username, or device..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#aaa' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
            {search && (
              <Typography sx={{ fontSize: 12, color: '#888', mt: 1 }}>
                Showing {filtered.length} of {alerts.length} registrations
              </Typography>
            )}
          </Box>

          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={28} sx={{ color: '#1a4d2e' }} />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 550 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Device</TableCell>
                    <TableCell>Registered at</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((a) => (
                    <TableRow
                      key={a.id}
                      sx={{
                        '&:last-child td': { border: 0 },
                        background: a.is_read ? 'transparent' : '#fafff8',
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {!a.is_read && (
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#1a4d2e', flexShrink: 0 }} />
                          )}
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: a.is_read ? 400 : 500 }}>
                              {a.full_name}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#888' }}>@{a.username}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneAndroidIcon sx={{ fontSize: 14, color: '#2d7a4f' }} />
                          <Typography sx={{ fontSize: 12 }}>{a.device_name || 'Unknown device'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {new Date(a.registered_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={a.is_read ? 'Read' : 'New'}
                          size="small"
                          sx={{
                            background: a.is_read ? '#f1efe8' : '#eaf3de',
                            color: a.is_read ? '#888' : '#27500a',
                            fontSize: 11,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: '#aaa', fontSize: 13 }}>
                        No device registrations found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </Card>
      </Box>
    </Box>
  );
}